import MainLayout from "../components/layouts/MainLayout";
import { getCookiePreferences } from "../helpers/cookies";
import Container from "../components/Container";
import SvgLogoBig from "../svg/SvgLogoBig";
import Title from "../components/Title";
import Typo from "../components/Typo";
import Button from "../components/Button";
import VideoThumb from "../components/VideoThumb";
import Image from "next/image";

import julianImage from "../public/img/julian.jpg";
import mapImage from "../public/img/media_map.png";
import teamImage from "../public/img/team.jpg";
import trainersImage from "../public/img/trainers.jpg";
import bioStrengthImage from "../public/img/biostrength.jpg";
import nextBurnImage from "../public/img/nextburn_thumb.jpg";
import Lightbox from "../components/Lightbox";
import LightboxBackground from "../components/LightboxBackground";
import { useEffect, useState } from "react";
import Video from "../components/Video";
import { useRouter } from "next/router";
import localized from "../locale/locales";

export default function Media({ hasCookiePreferences, cookiePreferences }) {
    const { locale } = useRouter();
    const { og } = localized[locale];

    const [showPopup, setShowPopup] = useState(false);
    const [activeImage, setActiveImage] = useState(null);
    const [activeVideo, setActiveVideo] = useState(null);
    const [hiddenNav, setHiddenNav] = useState(false);

    const [isZoomable, setIsZoomable] = useState(true);

    const openImage = (src) => {
        if (isZoomable) {
            setActiveImage(src);
            setHiddenNav(true);
        }
    }

    const openVideo = (src) => {
        setActiveVideo(src);
        setShowPopup(true);
        setHiddenNav(true);
    }

    const handleWindowResize = () => {
        setIsZoomable(window?.innerWidth >= 768 || false);
    };

    useEffect(() => {
        window?.addEventListener('resize', handleWindowResize);
        handleWindowResize();

        return () => window?.removeEventListener('resize', handleWindowResize);
    }, []);

    return (
        <MainLayout
            hasCookiePreferences={hasCookiePreferences}
            cookiePreferences={cookiePreferences}
            hiddenNav={hiddenNav}
            title={`Media`}
        >
            {/* Image Lightbox */}
            <LightboxBackground active={!!activeImage} onClick={() => {
                setHiddenNav(false);
                setActiveImage(null);
            }}>
                <div className="text-white m-auto cursor-zoom-out w-full text-center">
                    {activeImage && <Image src={activeImage} alt="" className="m-auto"
                        sizes="100vw"
                    />}
                </div>
            </LightboxBackground>

            {/* Video Lightbox */}
            <Lightbox active={showPopup} onClose={() => {
                setShowPopup(false);
                setActiveVideo(null);
                setHiddenNav(false);
            }}>
                <Video url={activeVideo} active={showPopup} />
            </Lightbox>


            <Container className="pt-12 md:pt-24">
                <div className="max-w-xl mx-auto text-center flex flex-col gap-10">
                    <div className="w-full max-w-2xs md:max-w-xs mx-auto">
                        <SvgLogoBig id="media" />
                    </div>
                    <header>
                        <Title Elem="h1" className="uppercase">
                            <span className="text-0">Next.Move - </span>
                            Moderní fitness club světové úrovně konečně v&nbsp;Praze
                        </Title>
                    </header>
                    <Typo>
                        Na začátku byla vášeň k pohybu a touha zpřístupnit fitness všem, kteří se chtějí cítit a vypadat
                        skvěle. Zakladatel Next.Move, Julián Jančík, se nechal inspirovat v zahraničí, kde dlouhodobě
                        žil, a rozhodl se přinést světový koncept cvičení a péče o zdraví do Čech.
                    </Typo>
                    <div>
                        <Button label="Media Package"
                            href="https://drive.google.com/drive/folders/1QzY8HGWPlUkZ6a3ixjiiu3MdXT5w76b6?usp=sharing"
                            target="_blank"
                            rel="nofollow noreferrer"
                        />
                    </div>
                </div>
            </Container>

            <div className="bg-split-vertical py-20">
                <Container>
                    <VideoThumb image={trainersImage}
                        showLabel={true}
                        className="h-64 sm:h-80 md:h-gallery"
                        onPlay={() => openVideo('/videos/first_timer.mp4')}
                    />
                </Container>
            </div>

            <div className="bg-black text-white">
                <Container>
                    <blockquote className="max-w-xl text-center mx-auto flex flex-col gap-10">
                        <p className="text-lg leading-loose">
                            „Chci pozvednout úroveň fitness v Česku a nasměrovat lidi, aby se cítili lépe po všech
                            stránkách. Velkou inspirací pro mě bylo Nizozemsko, které mě ve vnímání fitness hodně
                            posunulo a ukázalo nový směr, který mi v Česku chyběl. Rád bych odstartoval fitness
                            revoluci.“
                        </p>
                        <cite className="not-italic">
                            Julián Jančík, zakladatel
                        </cite>
                    </blockquote>
                </Container>
            </div>

            <div className="bg-split-vertical-reverse py-20 relative">
                <Container>
                    <div
                        className={`max-w-3xl mx-auto rounded-md overflow-hidden ${isZoomable ? 'cursor-zoom-in hover:scale-[1.05] transition-all duration-500' : ''}`}>
                        <Image src={julianImage}
                            onClick={() => openImage(julianImage)}
                            alt="Julián Jančík, zakladatel"
                            sizes="(min-width: 768px) 768px, 100vw"
                        />
                    </div>
                </Container>
            </div>

            <Container className="pb-28">
                <article className="max-w-3xl mx-auto flex flex-col gap-12">
                    <Typo>
                        Vlajková loď brandu Next.Move našla své místo v industriálním prostředí pražských Holešovic. Na
                        650m² najdete designové prostředí gymu s nejnovějšími technologiemi, unikátní studio s klubovou
                        atmosférou, útulný lounge s FuelBarem i zahradu.
                    </Typo>
                    <Typo>
                        “Přinášíme světovou úroveň fitness do lokálního kontextu. Propojujeme fitness trendy s
                        nejmodernější technologií pro jedinečný sportovní zážitek a dáváme tak cvičení nový rozměr”
                    </Typo>
                    <Typo>
                        Základní filozofií Next.Move je rovnováha mezi fyzickým a duševním zdravím s využitím
                        nejmodernějších technologií, síly komunity a zkušeností profesionálních trenérů. Pro zakladatele
                        Next.Move, Juliána Jančíka, je prioritou přinést změnu v oblasti fitness a zpřístupnit
                        pravidelné cvičení všem, kteří chtějí ve svém životě pozitivní změnu.
                    </Typo>

                    <Typo>
                        Holešovice jsou však pro Juliána a jeho tým teprve začátek. Next.Move se už teď rozrůstá nejen o
                        další prostory, ale i koncepty cvičení. “Nestojí za námi velká korporace, ale o to větší máme
                        srdce, odvahu a plány. V této chvíli designujeme nové fitness cluby ve spolupráci s těmi
                        nejprogresivnějšími developerskými projekty, které Praha nabízí. Do dvou let budou mít zákazníci
                        na výběr
                        z pěti poboček a nových, unikátních konceptů inspirovaných boxingem, funkčním tréninkem nebo
                        jógou. Těšit se můžou i na skupinové lekce Les Mills® v jedinečném klubovém prostředí.”
                        prozrazuje.
                    </Typo>

                    <div className="rounded-md overflow-hidden">
                        <Image src={mapImage}
                            alt="Map"
                            sizes="(min-width: 768px) 768px, 100vw"
                        />
                    </div>

                    <div className="flex flex-col gap-8">
                        <h2 className="text-lg font-semibold leading-none">
                            Next.Move přináší unikátní koncepty
                        </h2>
                        <Typo>
                            Uplynulé dva roky utvrdily zakladatele Next.Move v tom, že je stěžejní hledat dlouhodobé
                            řešení v oblasti zdraví a životního stylu. Pravidelné cvičení má nepřeberné množství
                            benefitů, lidé ale často z různých důvodů ani nezačnou. Koncept Next.Move přesně tyto důvody
                            řeší. Jedním z takových řešení je například BioCircuit. Představte si chytrou moderní
                            technologii, kde se jednotlivé stroje dokonale adaptují vašim potřebám a cílům a bezpečně
                            vás provedou každým cvikem. Klient tedy nemusí mít obavu, že by byl ve fitku ztracený nebo
                            se neobešel bez osobního trenéra. Je to ideální start vaší fitness cesty.
                        </Typo>
                    </div>

                    <div className="flex flex-col gap-8">
                        <h2 className="text-lg font-semibold leading-none">
                            Signature skupinová lekce Next.Burn
                        </h2>
                        <Typo>
                            (HIIT) High Intensity Interval Training lekce Next.Burn vás dostanou do varu a vtáhnou do
                            světa čiré energie, klubové hudby a světelné show. Spojení kardia na strojích SkillMil (Red
                            Dot Award) s prvky silového tréninku s činkami a plyo boxy vám pomůže zapojit všechny
                            svalové partie a spálit rekordní množství kalorií. Připravte se na jedinečný zážitek z HIIT
                            workoutu, který v Česku nemá obdoby. Tohle je ta nejčistší forma intenzity, síly a
                            vytrvalosti ve 45 minutách.
                        </Typo>
                    </div>

                    <VideoThumb image={nextBurnImage}
                        showLabel={true}
                        className="h-64 sm:h-80 md:h-video"
                        onPlay={() => openVideo('/videos/next_burn_web.mp4')}
                    />

                    <div className="flex flex-col gap-8">
                        <h2 className="text-lg font-semibold leading-none">
                            BioCircuit trénink z budoucnosti
                        </h2>
                        <Typo>
                            Nezáleží na věku ani fyzické kondici. Moderní způsob silového tréninku BioCircuit je pro
                            každého, tedy i pro naprosté začátečníky. Kruhový trénink na devíti strojích pod kontrolou
                            digitálního kouče je ideální vstupní bránou do světa silového tréninku. První nastavení
                            provede klient společně s profesionálním trenérem. Příště se jednoduše přihlásí pomocí
                            aplikace a stroj se automaticky nastaví, určí rozsah cvičení a po dokončení cviku doporučí
                            další stanoviště. Na procvičení celého těla stačí pouhých 22 minut.
                        </Typo>
                    </div>

                    <VideoThumb image={bioStrengthImage}
                        showLabel={true}
                        className="h-64 sm:h-80 md:h-video"
                        onPlay={() => openVideo('/videos/biostrength.mp4')}
                    />

                    <div className="flex flex-col gap-8">
                        <h2 className="text-lg font-semibold leading-none">
                            První pobočka na Dělnické
                        </h2>
                        <Typo>
                            Kromě zcela unikátních konceptů BioCircuit a Next.Burn nabízí Next.Move špičkově vybavené
                            zázemí pro osobní silový i kardio trénink se state of the art stroji od značek Technogym a
                            Eleiko. Po cvičení vám ve FuelBaru na recepci připraví vyladěný proteinový shake nebo
                            vzpruhu v podobě výběrové kávy Doubleshot. Odpočinek si poté můžete užít v příjemné lounge
                            zóně. Firemní zákazníci mohou využít výhodných členství, workshopů a skupinových lekcí nebo
                            cvičení postavených přímo na míru jejich týmům.
                        </Typo>
                    </div>

                    <div
                        className={`rounded-md overflow-hidden ${isZoomable ? 'cursor-zoom-in hover:scale-[1.05] transition-all duration-500' : ''}`}
                    >
                        <Image src={teamImage}
                            onClick={() => openImage(teamImage)}
                            alt="Tým Next.Move"
                            sizes="(min-width: 768px) 768px, 100vw"
                        />
                    </div>

                    <Typo>
                        Next.Move je jedinečné místo v Praze, kde odhalíte svůj sportovní potenciál. Vaše zdraví a dobrý
                        pocit z pohybu jsou pro nás rozhodující, a proto je vždy upřednostňujeme před výkonem. Ať už
                        patříte mezi fanoušky tradičních postupů, nebo jste milovníky inovativních metod, s námi si
                        trénink užijete!
                    </Typo>
                    <Typo>
                        Přijďte si to vyzkoušet na vlastní kůži na Dělnickou 9 na Praze 7.
                    </Typo>
                    <Typo>
                        První návštěva je zdarma.
                    </Typo>
                </article>
            </Container>

        </MainLayout>
    )
}


export async function getServerSideProps({ req, res }) {
    const cookiePreferences = getCookiePreferences(req);

    return {
        props: {
            ...cookiePreferences
        }
    }
}
